// NationalPartyShareBar.jsx
import { Box } from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  LabelList,
} from "recharts";

// Colours to match Dutch political parties
const PARTY_COLOURS = {
  D66: "#2cc800",
  PVV: "#043555",
  VVD: "#0e2783",
  GLpvdA: "#920c19",
  CDA: "#339933",
  OtherRight: "#7393B3",
  OtherLeft: "#997570",
  OtherCentre: "#D3D3D3"
};

// Custom label: party on first line, % on second line (white, centred)
function SegmentLabel({ x, y, width, height, value, name }) {
  // Hide labels if the slice is too thin
  if (width < 20) return null;

  const cx = x + width / 2;
  const cy = y + height / 2;

  const pctText = `${Math.round(value)}%`;

  // If very narrow, only show % to avoid wrapping
  const onlyPct = width < 10;

  return (
    <g style={{ pointerEvents: "none" }}>
      {!onlyPct && (
        <text
          x={cx}
          y={cy - 6}
          textAnchor="middle"
          dominantBaseline="central"
          fill="#fff"
          fontWeight="700"
          fontSize="10"
        >
          {name}
        </text>
      )}
      <text
        x={cx}
        y={onlyPct ? cy : cy + 8}
        textAnchor="middle"
        dominantBaseline="central"
        fill="#fff"
        fontWeight="600"
        fontSize="10"
      >
        {pctText}
      </text>
    </g>
  );
}
// matcher because named the columns stupidly
function getGroupKeys(polLevel) {
  const forward =
    polLevel === "past"
      ? {
          D66: "past_vote_D66",
          CDA: "past_vote_CDA",
          GLpvdA: "past_vote_GLpvdA",
          OtherCentre: "past_vote_OtherCentre",
          OtherLeft: "past_vote_OtherLeft",
          OtherRight: "past_vote_OtherRight",
          PVV: "past_vote_PVV",
          VVD: "past_vote_VVD",
        }
      : (() => {
          const level = polLevel === "LE" ? "LE" : "GE";

          return {
            D66: `D66_${level}_p`,
            PVV: `PVV_${level}_p`,
            VVD: `VVD_${level}_p`,
            GLpvdA: `GLpvdA_${level}_p`,
            CDA: `CDA_${level}_p`,
            ...(polLevel === "LE" ? { Local: "Local_LE_p" } : {}), // ✅ only when LE
            OtherRight: `OtherRight_${level}_p`,
            OtherLeft: `OtherLeft_${level}_p`,
            OtherCentre: `OtherCentre_${level}_p`,
          };
        })();

  // invert it: longKey -> shortName
  return Object.fromEntries(
    Object.entries(forward).map(([shortName, longKey]) => [longKey, shortName])
  );
}

export default function NationalPartyShareBar({
  metricRowsConst,
  metricRowsNat,
  selectedFeature,
  WIDTH,
  polLevel
}) {
  // If neither dataset exists yet, render nothing
  if (!metricRowsNat && !metricRowsConst){
    return null;
  } 

  const safeArray = (v) => (Array.isArray(v) ? v : []);

  const constRows = safeArray(metricRowsConst);
  const natRows = safeArray(metricRowsNat);

  // Choose row safely
  let row = {};

  const hasSelectedAreaId =
    selectedFeature != null && selectedFeature.area_id != null;

  if (hasSelectedAreaId && constRows.length > 0) {
    const selectedId = selectedFeature.area_id;

    // find() returns undefined if no match; fallback to {}
    row =
      constRows.find((g) => g?.area_id === selectedId) ??
      {};

    // Optional: helpful debug
    // if (!row || Object.keys(row).length === 0) {
    //   console.warn("No const row found for area_id:", selectedFeature.area_id);
    // }
  } else {
    row = natRows[0] ?? {};
  }

  const toPct = (v) => {
    const num = Number(v);
    return Number.isFinite(num) ? num : 0;
  };

// longKey -> shortName (e.g. "D66_GE_p" -> "D66", or "past_vote_D66" -> "D66")
const longToShort = getGroupKeys(polLevel);

// Build a single data point that Recharts will stack into one bar
const dataPoint = { id: "NationalRow" };

for (const [longKey, shortName] of Object.entries(longToShort)) {
  dataPoint[shortName] = toPct(row?.[longKey]);
}

const data = [dataPoint];


  const SERIES =  polLevel === "LE" ? [
    "GLpvdA",
    "D66",
    "OtherLeft",
    "OtherCentre",
    "Local",
    "OtherRight",
    "CDA",
    "VVD",
    "PVV"
  ] : [
    "GLpvdA",
    "D66",
    "OtherLeft",
    "OtherCentre",
    "OtherRight",
    "CDA",
    "VVD",
    "PVV"
  ];

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignContent: "center",
        width: WIDTH,
        px: 0,
      }}
    >
      <ResponsiveContainer
        width="100%"
        height={48}
        margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
      >
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
          barCategoryGap={0}
        >
          <XAxis type="number" domain={[0, 100]} hide />
          <YAxis type="category" dataKey="id" hide />

          {SERIES.map((key, idx) => (
            <Bar
              key={key}
              dataKey={key}
              stackId="TOTAL"
              fill={PARTY_COLOURS?.[key] || "#ccc"}
              stroke="#ffffff"
              strokeWidth={2}
              radius={
                idx === 0
                  ? [6, 0, 0, 6]
                  : idx === SERIES.length - 1
                  ? [0, 6, 6, 0]
                  : [0, 0, 0, 0]
              }
              isAnimationActive={false}
            >
              <LabelList
                dataKey={key}
                content={(props) => (
                  <SegmentLabel {...props} name={key} />
                )}
              />
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}
